import isEqual from "lodash-es/isEqual";
import { useEffect, useMemo, useState } from "react";

interface UseInfinitePageItemsOptions<Item> {
  /** Items for the current page from the API. */
  pageItems: Item[];
  /** 1-based page number. Resets the accumulated list when it returns to `1`. */
  pageNo: number;
  /** Items per page — used as `hasMore` fallback when `total` is absent. NOTE: Changing pageSize does not reset accumulated items; caller must reset pageNo to 1. */
  pageSize: number;
  /** Total item count. When provided, `hasMore` is `items.length < total`; otherwise falls back to `pageItems.length >= pageSize`. */
  total?: number;
  /** Returns a stable unique key per item. Must be stable (`useCallback`). */
  getItemId: (item: Item) => string | number | undefined;
}

interface State<Item> {
  /** Accumulated items across all loaded pages, deduped by `getItemId`. */
  items: Item[];
  /** `true` when more pages are available. */
  hasMore: boolean;
}

/**
 * Accumulates paginated API responses into a flat list for infinite-scroll UIs.
 * Resets on `pageNo === 1`, deduplicates via `getItemId`, and derives `hasMore` from
 * `total` when available. Pair with {@link useInfiniteScroll} to drive page increments.
 */
const useInfinitePageItems = <Item>({
  pageItems,
  pageNo,
  pageSize,
  total,
  getItemId,
}: UseInfinitePageItemsOptions<Item>): State<Item> => {
  const [items, setItems] = useState<Item[]>([]);

  useEffect(() => {
    setItems(prev => {
      const base = pageNo === 1 ? [] : prev;

      const existingIds = new Set(base.map(getItemId));
      const newItems = pageItems.filter(
        item => !existingIds.has(getItemId(item)),
      );

      const next = newItems.length === 0 ? base : [...base, ...newItems];

      // Preserve the previous reference when nothing changed, so an unstable
      // `pageItems` identity can never drive a render loop.
      if (next.length === prev.length && isEqual(next, prev)) {
        return prev;
      }

      return next;
    });
  }, [pageItems, pageNo, getItemId]);

  const hasMore = useMemo(() => {
    if (typeof total === "number") {
      return items.length < total;
    }

    return pageItems.length >= pageSize;
  }, [items.length, total, pageItems.length, pageSize]);

  return { items, hasMore };
};

export default useInfinitePageItems;
